import React, { useState, useEffect, useContext } from "react";
import "./Report.css";
import { ProjectContext } from "../../App.js";
import api from "../../api/axiosConfig.js";
import { GoEye } from "react-icons/go";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import DatePicker from "react-datepicker";

const UsersPage = () => {
  const { isNavBarOpen } = useContext(ProjectContext);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [performance, setPerformance] = useState({});
  const [showTable, setShowTable] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedProject, setSelectedProject] = useState("");
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = users.length;

  // Fetch users
  useEffect(() => {
    api
      .get("/api/userspage")
      .then((res) => {
        setUsers(res.data.sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch((err) => console.error("Error fetching users:", err));
  }, []);

  // Fetch user performance
  const fetchPerformance = (userId) => {
    const params = new URLSearchParams();

    // ✅ Convert Date objects to yyyy-MM-dd before sending
    const formatDate = (date) => {
      if (!date) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    if (selectedProject && selectedProject !== "all") {
      params.append("projectId", selectedProject);
    }
    if (startDate && endDate) {
      params.append("from", formatDate(startDate));
      params.append("to", formatDate(endDate));
    }

    api
      .get(`/api/userspage/${userId}/performance?${params.toString()}`)
      .then((res) => setPerformance(groupByProject(res.data)))
      .catch((err) => console.error("Error fetching performance:", err));
  };

  // Group performance by project
  const groupByProject = (data) => {
    const grouped = {};

    data.forEach((item) => {
      const key = item.project_id || "no_project"; // fallback if null (e.g. Non Productive)

      if (!grouped[key]) {
        grouped[key] = {
          project_id: item.project_id,
          project_name: item.project_name,
          project_no: item.projectno,
          logs: [],
          totalProjectHours: 0,
        };
      }

      grouped[key].logs.push(item);
      grouped[key].totalProjectHours += parseFloat(item.total_hours || 0);
    });

    return grouped;
  };

  // Handle search input
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  // Pagination Logic
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(search.toLowerCase()) ||
      user.email.toLowerCase().includes(search.toLowerCase()) ||
      user.employeecode.toLowerCase().includes(search.toLowerCase())
  );
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  // Find the index of the currently selected user in the filtered list
  const selectedIndex = filteredUsers.findIndex(
    (user) => user.id === selectedUser?.id
  );

  const handleNextUser = () => {
    if (selectedIndex < filteredUsers.length - 1) {
      const nextUser = filteredUsers[selectedIndex + 1];
      setSelectedUser(nextUser);
      fetchPerformance(nextUser.id);
    }
  };

  const handlePrevUser = () => {
    if (selectedIndex > 0) {
      const prevUser = filteredUsers[selectedIndex - 1];
      setSelectedUser(prevUser);
      fetchPerformance(prevUser.id);
    }
  };

  const handleExport = () => {
    // 1. Format date & time as dd-mm-yyyy hh:mm am
    const now = new Date();

    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();

    let hours = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should become 12

    const formattedDate = `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;

    // 2. User details
    const userDetails = [
      ["Performance of", selectedUser.name],
      ["Designation", selectedUser.designation],
      ["Emp Code", selectedUser.employeecode],
      ["Project Count", selectedUser.project_count],
      ["Exported On", formattedDate],
    ];

    // 3. Create sheet with user details
    const ws = XLSX.utils.aoa_to_sheet(userDetails);

    // 4. Get table data
    const table = document.getElementById("my-table");
    const wsTable = XLSX.utils.table_to_sheet(table);
    const tableData = XLSX.utils.sheet_to_json(wsTable, { header: 1 });

    // 5. Append table below details
    XLSX.utils.sheet_add_aoa(ws, [[""]], { origin: -1 }); // spacer row
    XLSX.utils.sheet_add_aoa(ws, tableData, { origin: -1 });

    // 6. Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, selectedUser.name);

    // 7. Export file
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `${selectedUser.name}_report.xlsx`);
  };
  useEffect(() => {
    if (selectedUser) {
      fetchPerformance(selectedUser.id);
    }
  }, [selectedProject, startDate, endDate]);

  return (
    <div className={isNavBarOpen ? "user-report-con" : "user-report-con-close"}>
      <h3 style={{ margin: "0", marginBottom: "15px", marginTop: "20px" }}>
        Users Summary
      </h3>

      {/* Search Input */}
      <input
        className="user-report-search"
        placeholder="Search by name or email..."
        value={search}
        onChange={handleSearch}
      />

      {/* User Table View */}
      {showTable ? (
        <div
          className={
            isNavBarOpen
              ? "user-report-table-wrapper"
              : "user-report-table-wrapper-close"
          }
        >
          <table
            className={
              isNavBarOpen ? "user-report-table" : "user-report-table-close"
            }
          >
            <thead>
              <tr>
                <th>Emp Code</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Projects</th>
                <th>Total Hours</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.employeecode}</td>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>{user.designation}</td>
                  <td>{user.project_count}</td>
                  <td>{Math.round(user.total_hours)}</td>
                  <td>
                    <div
                      className="action-view-con"
                      onClick={() => {
                        setSelectedUser(user);
                        fetchPerformance(user.id);
                        setShowTable(false);
                      }}
                    >
                      <GoEye
                        className="action-view"
                        style={{ backgroundColor: "transparent" }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // Individual User Details
        selectedUser &&
        (() => {
          function parseDurationToHours(duration) {
            const [hours, minutes] = duration.split(":").map(Number);
            return hours + minutes / 60;
          }

          let totalNonProductiveHours = 0;
          let totalProductiveHours = 0;
          let nonAttendanceHours = 0;

          Object.keys(performance).forEach((projectName) => {
            performance[projectName].logs.forEach((log) => {
              const logHours = parseDurationToHours(log.total_duration);

              if (log.phase_name === "Non Productive") {
                if (
                  log.logtitle === "L-Leave " ||
                  log.logtitle === "P-Permission"
                ) {
                  nonAttendanceHours += logHours;
                } else {
                  totalNonProductiveHours += logHours;
                }
              } else {
                totalProductiveHours += logHours;
              }
            });
          });

          return (
            <div className="individual-user-report-con">
              <div className="individual-user-report">
                <div className="ind-user-header">
                  <p className="user-report-details">
                    Performance of {selectedUser.name}
                    <span>Designation: {selectedUser.designation}</span>
                    <span>Emp Code : {selectedUser.employeecode}</span>
                    <span>Project Count: {selectedUser.project_count}</span>
                    <span style={{ color: "orange" }}>
                      Non-Productive: {totalNonProductiveHours.toFixed(2)}
                    </span>
                    <span style={{ color: "green" }}>
                      Productive: {totalProductiveHours.toFixed(2)}
                    </span>
                    <span style={{ color: "#a52a2a" }}>
                      Absent: {nonAttendanceHours.toFixed(2)}
                    </span>
                  </p>
                  {selectedUser.picture && selectedUser.picture !== "null" ? (
                    <img
                      className="user-dp-urp"
                      src={
                        selectedUser.picture
                          ? `${api.defaults.baseURL}${selectedUser.picture}`
                          : "/default-avatar.png"
                      }
                      alt="profile"
                    />
                  ) : (
                    <span className="user-initial">
                      {selectedUser.name
                        .charAt(selectedUser.name.length - 1)
                        .toUpperCase() +
                        selectedUser.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="user-report-pagination">
                  <button
                    className="user-report-next-btn"
                    onClick={handlePrevUser}
                    disabled={selectedIndex === 0}
                    style={{ border: "none" }}
                  >
                    <MdKeyboardArrowLeft
                      style={{ backgroundColor: "transparent" }}
                    />
                  </button>
                  <span style={{ top: "-2px", position: "relative" }}>
                    User {selectedIndex + 1} of {filteredUsers.length}
                  </span>
                  <button
                    className="user-report-next-btn"
                    onClick={handleNextUser}
                    disabled={selectedIndex === filteredUsers.length - 1}
                    style={{ border: "none" }}
                  >
                    <MdKeyboardArrowRight
                      style={{ backgroundColor: "transparent" }}
                    />
                  </button>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <button
                    className="table-view-btn"
                    onClick={() => {
                      setSelectedUser(null);
                      setShowTable(!showTable);
                      setSelectedProject("");
                      setStartDate(null);
                      setEndDate(null);
                    }}
                  >
                    Back
                  </button>
                  <button className="table-export-btn" onClick={handleExport}>
                    Export to Excel
                  </button>
                  <div className="user-filter-container">
                    <h3>Filter By</h3>
                    {(selectedProject || startDate || endDate) && (
                      <button
                        className="report-clear-filter"
                        onClick={() => {
                          setSelectedProject("");
                          setStartDate(null);
                          setEndDate(null);
                          if (selectedUser) fetchPerformance(selectedUser.id);
                        }}
                      >
                        Clear Filter
                      </button>
                    )}
                    <select
                      className="user-filter-input"
                      value={selectedProject}
                      onChange={(e) => setSelectedProject(e.target.value)}
                    >
                      <option value="">Select Project</option>

                      {Object.values(performance)
                        .sort((a, b) => {
                          const special = ["Non Productive", "Estimate"];
                          const aSpecial = special.includes(a.project_name);
                          const bSpecial = special.includes(b.project_name);

                          if (aSpecial && !bSpecial) return 1;
                          if (!aSpecial && bSpecial) return -1;
                          return a.project_name.localeCompare(b.project_name);
                        })
                        // ✅ Make sure to group by project_id to avoid duplicates
                        .filter(
                          (p, index, self) =>
                            index ===
                            self.findIndex(
                              (obj) => obj.project_id === p.project_id
                            )
                        )
                        .map((p) => (
                          <option key={p.project_id} value={p.project_id}>
                            {p.project_no
                              ? `${p.project_no} - ${p.project_name}`
                              : p.project_name}
                          </option>
                        ))}
                    </select>
                    <div>
                      <DatePicker
                        className="date-filter-report"
                        dateFormat={"dd-MM-yyyy"}
                        selected={startDate}
                        onChange={setStartDate}
                        placeholderText="Start Date"
                      />
                      <DatePicker
                        className="date-filter-report"
                        dateFormat={"dd-MM-yyyy"}
                        selected={endDate}
                        onChange={setEndDate}
                        placeholderText="End Date"
                      />
                    </div>
                  </div>
                </div>
                <div
                  className={
                    isNavBarOpen
                      ? "individual-user-table-wrapper"
                      : "individual-user-table-wrapper-close"
                  }
                >
                  <table
                    id="my-table"
                    className={
                      isNavBarOpen
                        ? "individual-user-table"
                        : "individual-user-table-close"
                    }
                  >
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Project Hours</th>
                        <th>Phase</th>
                        <th>Log Title</th>
                        <th>Log Count</th>
                        <th>Total Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(performance).map((projectId) => {
                        const project = performance[projectId];
                        const projectData = project.logs;
                        const totalProjectHours =
                          project.totalProjectHours.toFixed(2);

                        const phaseGroups = {};
                        projectData.forEach((log) => {
                          if (!phaseGroups[log.phase_name]) {
                            phaseGroups[log.phase_name] = [];
                          }
                          phaseGroups[log.phase_name].push(log);
                        });

                        return Object.keys(phaseGroups).map(
                          (phaseName, phaseIndex) => {
                            const phaseData = phaseGroups[phaseName];

                            return phaseData.map((log, index) => (
                              <tr key={`${projectId}-${phaseName}-${index}`}>
                                {phaseIndex === 0 && index === 0 && (
                                  <>
                                    <td
                                      rowSpan={projectData.length}
                                      style={{
                                        verticalAlign: "top",
                                        textAlign: "left",
                                      }}
                                    >
                                      {log.projectno
                                        ? `${log.projectno} - ${project.project_name}`
                                        : project.project_name || ""}
                                      {/* ✅ Correct name */}
                                    </td>
                                    <td
                                      rowSpan={projectData.length}
                                      style={{
                                        verticalAlign: "top",
                                        textAlign: "center",
                                        color:
                                          phaseName === "Non Productive"
                                            ? "orange"
                                            : "green",
                                      }}
                                    >
                                      {totalProjectHours}
                                    </td>
                                  </>
                                )}
                                {index === 0 && (
                                  <td
                                    rowSpan={phaseData.length}
                                    style={{
                                      verticalAlign: "top",
                                      textAlign: "left",
                                    }}
                                  >
                                    {log.phaseno
                                      ? `${log.phaseno} - ${phaseName}`
                                      : phaseName || ""}
                                  </td>
                                )}
                                <td>{log.logtitle}</td>
                                <td>{log.log_count}</td>
                                <td
                                  style={{
                                    color:
                                      phaseName === "Non Productive"
                                        ? "orange"
                                        : "green",
                                  }}
                                >
                                  {log.total_duration}
                                </td>
                              </tr>
                            ));
                          }
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()
      )}
    </div>
  );
};

export default UsersPage;
